import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "model";

export type ProgrammeActions = {
  guid: string;
  programmeName: string;
  description: string;
  filloutId?: string;
  filloutParameters: Record<string, string | undefined>;
  welcomeText: string;
  submitted: boolean;
};

export default class ProgrammeActionsAPI {
  async getIds(): Promise<Array<ProgrammeActions>> {
    const res = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/v2/programmesActions`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    const resultJson = await res.json();
    return resultJson as ProgrammeActions[];
  }
}

const programmeActionsApi = new ProgrammeActionsAPI();

export const fetchAllProgrammeActions = createAsyncThunk(
  "programmeActions/fetchIds",
  async () => {
    const fetchedDashboardIds = await programmeActionsApi.getIds();
    return fetchedDashboardIds;
  }
);

const programmeActionsAdapter = createEntityAdapter<ProgrammeActions>({
  selectId: (projectDashboard) => projectDashboard.guid,
});

export const {
  selectAll: selectAllProgrammeActions,
  selectById: selectProgrammeActionsById,
  selectEntities: selectProgrammeActionsEntities,
  selectIds: selectProgrammeActionsIds,
} = programmeActionsAdapter.getSelectors(
  (state: RootState) => state.programmeActions
);

type Status = "success" | "error" | null;
type LoadingState = "idle" | "pending";

export type programmeActionsState = EntityState<ProgrammeActions> & {
  loading: LoadingState;
  error: null;
  status: Status;
};

const initialProgrammeActionsState: programmeActionsState =
  programmeActionsAdapter.getInitialState({
    loading: "pending",
    error: null,
    status: null,
  });

const programmeActionsSlice = createSlice({
  name: "programmeActions",
  initialState: initialProgrammeActionsState,
  reducers: {
    resetProgrammeActions: () => initialProgrammeActionsState,
  },
  extraReducers: {
    [fetchAllProgrammeActions.fulfilled.type]: (state, { payload }) => {
      for (const programmeActions of payload) {
        programmeActionsAdapter.upsertOne(state, {
          guid: programmeActions.guid,
          programmeName: programmeActions.programmeName,
          filloutId: programmeActions.filloutId,
          filloutParameters: programmeActions.filloutParameters,
          description: programmeActions.description,
          welcomeText: programmeActions.welcomeText,
          submitted: programmeActions.submitted,
        });
      }
      state.loading = "idle";
    },
    [fetchAllProgrammeActions.pending.type]: (state, action) => {
      state.loading = "pending";
    },
    [fetchAllProgrammeActions.rejected.type]: (state, action) => {
      state.loading = "idle";
      state.error = action.error;
    },
  },
});

const { actions, reducer } = programmeActionsSlice;

export const { resetProgrammeActions } = actions;
export const programmeActionsReducer = reducer;
